import { makeRequest } from "../../../utils/helpers/helperMethods/makeRequest";
import {
  IOrganisation,
  IOrganisationLookup,
  IOrganisationRequest,
  IOrganisationsRequest,
  IOrganisationsResponse,
  ISubscriptionPlan,
} from "../models/IOrganisation";

const baseUrl = `${process.env.REACT_APP_API_TODO_LIST_ENDPOINT}/organizations`;

const getAccessToken = () => localStorage.getItem("AccessToken");

// The API returns { Message } on errors; model validation errors arrive as a serialized JSON map.
const getErrorMessage = async (response: Response, fallback: string) => {
  try {
    const body = await response.json();
    const message: string | undefined = body?.Message ?? body?.message;
    return message && !message.trim().startsWith("{") ? message : fallback;
  } catch {
    return fallback;
  }
};

export const OrganisationService = {
  getOrganisations: async ({
    pageNumber,
    pageSize,
    searchText = "",
  }: IOrganisationsRequest): Promise<IOrganisationsResponse> => {
    const query = new URLSearchParams({
      PageNumber: String(pageNumber),
      PageSize: String(pageSize),
    });
    if (searchText.trim()) query.append("SearchText", searchText.trim());

    const response = await makeRequest(
      getAccessToken(),
      `${baseUrl}?${query.toString()}`,
      "GET",
      null,
      ""
    );

    if (!response.ok) {
      throw new Error(`Organisations get failed: ${response.status}`);
    }

    return response.json();
  },

  getOrganisation: async (id: number | string): Promise<IOrganisation> => {
    const response = await makeRequest(
      getAccessToken(),
      `${baseUrl}/${id}`,
      "GET",
      null,
      ""
    );

    if (!response.ok) {
      throw new Error(`Organisation get failed: ${response.status}`);
    }

    return response.json();
  },

  getOrganisationLookups: async (): Promise<IOrganisationLookup[]> => {
    const response = await makeRequest(
      getAccessToken(),
      `${baseUrl}/lookup`,
      "GET",
      null,
      ""
    );

    if (!response.ok) {
      throw new Error(`Organisation lookups get failed: ${response.status}`);
    }

    return response.json();
  },

  getSubscriptionPlans: async (): Promise<ISubscriptionPlan[]> => {
    const response = await makeRequest(
      getAccessToken(),
      `${process.env.REACT_APP_API_TODO_LIST_ENDPOINT}/subscription-plans`,
      "GET",
      null,
      ""
    );

    if (!response.ok) {
      throw new Error(`Subscription plans get failed: ${response.status}`);
    }

    return response.json();
  },

  createOrganisation: async (
    organisation: IOrganisationRequest
  ): Promise<IOrganisation> => {
    const response = await makeRequest(
      getAccessToken(),
      baseUrl,
      "POST",
      organisation,
      ""
    );

    if (!response.ok) {
      throw new Error(
        await getErrorMessage(response, "Organisation creation failed")
      );
    }

    return response.json();
  },

  updateOrganisation: async (
    id: number | string,
    organisation: IOrganisationRequest
  ): Promise<IOrganisation> => {
    const response = await makeRequest(
      getAccessToken(),
      `${baseUrl}/${id}`,
      "PUT",
      { ...organisation, id: Number(id) },
      ""
    );

    if (!response.ok) {
      throw new Error(
        await getErrorMessage(response, "Organisation update failed")
      );
    }

    return response.json();
  },

  deleteOrganisation: async (id: number | string): Promise<void> => {
    const response = await makeRequest(
      getAccessToken(),
      `${baseUrl}/${id}`,
      "DELETE",
      null,
      ""
    );

    if (!response.ok) {
      throw new Error(
        await getErrorMessage(response, "Organisation delete failed")
      );
    }
  },
};
